import { useCallback, useEffect, useRef, useState } from 'react';
import {
  createAnswer,
  updateAnswer,
  type SaveAnswerPayload,
} from 'src/services/RenterQuestionnaire.service';

export type SaveStatus = 'idle' | 'scheduled' | 'saving' | 'saved' | 'error';

interface UseQuestionnaireAutosaveOptions {
  surveyId: number;
  answerId: number | null;
  qitId: number;
  answerGroupId?: number;
  debounceMs?: number;
}

interface UseQuestionnaireAutosaveReturn {
  saveStatus: SaveStatus;
  saveError: string | null;
  triggerSave: (payload: Omit<SaveAnswerPayload, 'qit_id'>) => void;
  triggerImmediateSave: (payload: Omit<SaveAnswerPayload, 'qit_id'>) => void;
  cancelPending: () => void;
}

export function useQuestionnaireAutosave({
  surveyId,
  answerId,
  qitId,
  answerGroupId,
  debounceMs = 750,
}: UseQuestionnaireAutosaveOptions): UseQuestionnaireAutosaveReturn {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);

  const latestRef = useRef({ surveyId, answerId, qitId, answerGroupId });
  latestRef.current = { surveyId, answerId, qitId, answerGroupId };

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelPending();
      abortRef.current = true;
    };
  }, [cancelPending]);

  const fireSave = useCallback(
    async (payload: Omit<SaveAnswerPayload, 'qit_id'>) => {
      abortRef.current = false;
      setSaveStatus('saving');
      setSaveError(null);

      const current = latestRef.current;

      try {
        if (current.answerId) {
          await updateAnswer(current.surveyId, current.answerId, {
            ...payload,
            answer_group_id: payload.answer_group_id ?? current.answerGroupId,
          });
        } else {
          await createAnswer(current.surveyId, {
            ...payload,
            qit_id: current.qitId,
            answer_group_id: payload.answer_group_id ?? current.answerGroupId,
          });
        }
        if (!abortRef.current) {
          setSaveStatus('saved');
        }
      } catch (err: unknown) {
        if (!abortRef.current) {
          const message =
            err instanceof Error ? err.message : 'Failed to save answer';
          setSaveStatus('error');
          setSaveError(message);
        }
      }
    },
    []
  );

  const triggerSave = useCallback(
    (payload: Omit<SaveAnswerPayload, 'qit_id'>) => {
      cancelPending();
      setSaveStatus('scheduled');
      timerRef.current = setTimeout(() => {
        fireSave(payload);
      }, debounceMs);
    },
    [cancelPending, fireSave, debounceMs]
  );

  const triggerImmediateSave = useCallback(
    (payload: Omit<SaveAnswerPayload, 'qit_id'>) => {
      cancelPending();
      fireSave(payload);
    },
    [cancelPending, fireSave]
  );

  return {
    saveStatus,
    saveError,
    triggerSave,
    triggerImmediateSave,
    cancelPending,
  };
}
